import { supabase } from "@/integrations/supabase/client";
import type {
  Opportunity,
  OpportunityInsert,
  OpportunityStatus,
  OpportunityUpdate,
} from "./opportunityTypes";
import { ACTIONABLE_STATUSES } from "./opportunityConstants";

export async function fetchOpportunities(companyId: number): Promise<Opportunity[]> {
  const { data, error } = await supabase
    .from("opportunities")
    .select("*")
    .eq("company_id", companyId)
    .order("deadline", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
}

export async function createOpportunity(payload: OpportunityInsert): Promise<Opportunity> {
  const { data, error } = await supabase
    .from("opportunities")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateOpportunity(
  id: string,
  patch: OpportunityUpdate,
): Promise<Opportunity> {
  const { data, error } = await supabase
    .from("opportunities")
    .update(patch)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function setOpportunityStatus(id: string, status: OpportunityStatus) {
  return updateOpportunity(id, { status });
}

export async function deleteOpportunity(id: string): Promise<void> {
  const { error } = await supabase.from("opportunities").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Runs the SECOP sweep on demand. The same function is scheduled twice weekly by
 * pg_cron, so this is only for "check right now" — it is idempotent either way.
 */
export async function runSecopSweep(companyId: number, daysBack = 5): Promise<number> {
  const { data, error } = await supabase.rpc("sweep_secop_opportunities", {
    p_company_id: companyId,
    p_days_back: daysBack,
  });

  if (error) throw error;
  return data ?? 0;
}

/** Whole days until the deadline. Negative means it has already passed. */
export function daysUntil(deadline: string | null): number | null {
  if (!deadline) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${deadline}T00:00:00`);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/** Still open, still ours to lose, and closing soon. */
export function isUrgent(o: Opportunity, withinDays: number): boolean {
  if (!ACTIONABLE_STATUSES.includes(o.status)) return false;
  const days = daysUntil(o.deadline);
  return days !== null && days >= 0 && days <= withinDays;
}

/** Open, actionable, and the date has already gone. These need triage now. */
export function isOverdue(o: Opportunity): boolean {
  if (!ACTIONABLE_STATUSES.includes(o.status)) return false;
  const days = daysUntil(o.deadline);
  return days !== null && days < 0;
}

export function formatCop(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "—";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `$${Math.round(value / 1_000_000)}M`;
  return `$${value.toLocaleString("es-CO")}`;
}
